import Layout from "../components/Layout";
import NotificationSettings from "../pwa/NotificationSettings";
import { usePWAContext } from "../pwa/PWAProvider";
import {
  MdGetApp, MdWifi, MdWifiOff,
  MdSystemUpdate, MdPhoneAndroid,
  MdDesktopMac, MdNotifications,
} from "react-icons/md";

const PWASettings = () => {
  const {
    isInstallable,
    isInstalled,
    isOnline,
    updateAvailable,
    installPWA,
    applyUpdate,
  } = usePWAContext();

  const features = [
    {
      icon: <MdWifi size={22} className="text-blue-500" />,
      title: "Offline Support",
      desc: "Access cached dashboard data even without internet connection",
      status: true,
    },
    {
      icon: <MdNotifications size={22} className="text-blue-500" />,
      title: "Push Notifications",
      desc: "Receive instant alerts when devices go offline or anomalies are detected",
      status: true,
    },
    {
      icon: <MdPhoneAndroid size={22} className="text-blue-500" />,
      title: "Mobile Friendly",
      desc: "Responsive design that works on any screen size",
      status: true,
    },
    {
      icon: <MdDesktopMac size={22} className="text-blue-500" />,
      title: "Desktop App",
      desc: "Install as a desktop app for quick access without a browser",
      status: true,
    },
  ];

  return (
    <Layout>
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800">PWA Settings</h2>
        <p className="text-sm text-gray-500">
          Progressive Web App configuration and status
        </p>
      </div>

      {/* Status Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl shadow p-4 text-center">
          <div
            className={`w-10 h-10 rounded-full mx-auto flex items-center justify-center mb-2 ${
              isOnline ? "bg-green-100" : "bg-red-100"
            }`}
          >
            {isOnline ? (
              <MdWifi size={20} className="text-green-600" />
            ) : (
              <MdWifiOff size={20} className="text-red-600" />
            )}
          </div>
          <p className="text-xs text-gray-500">Connection</p>
          <p
            className={`text-sm font-semibold ${
              isOnline ? "text-green-600" : "text-red-600"
            }`}
          >
            {isOnline ? "Online" : "Offline"}
          </p>
        </div>

        <div className="bg-white rounded-xl shadow p-4 text-center">
          <div
            className={`w-10 h-10 rounded-full mx-auto flex items-center justify-center mb-2 ${
              isInstalled ? "bg-green-100" : "bg-gray-100"
            }`}
          >
            <MdGetApp
              size={20}
              className={isInstalled ? "text-green-600" : "text-gray-500"}
            />
          </div>
          <p className="text-xs text-gray-500">App Status</p>
          <p
            className={`text-sm font-semibold ${
              isInstalled ? "text-green-600" : "text-gray-600"
            }`}
          >
            {isInstalled ? "Installed" : "Not Installed"}
          </p>
        </div>

        <div className="bg-white rounded-xl shadow p-4 text-center">
          <div
            className={`w-10 h-10 rounded-full mx-auto flex items-center justify-center mb-2 ${
              "serviceWorker" in navigator ? "bg-green-100" : "bg-red-100"
            }`}
          >
            <MdSystemUpdate
              size={20}
              className={
                "serviceWorker" in navigator
                  ? "text-green-600"
                  : "text-red-600"
              }
            />
          </div>
          <p className="text-xs text-gray-500">Service Worker</p>
          <p
            className={`text-sm font-semibold ${
              "serviceWorker" in navigator
                ? "text-green-600"
                : "text-red-600"
            }`}
          >
            {"serviceWorker" in navigator ? "Active" : "Not Supported"}
          </p>
        </div>

        <div className="bg-white rounded-xl shadow p-4 text-center">
          <div
            className={`w-10 h-10 rounded-full mx-auto flex items-center justify-center mb-2 ${
              updateAvailable ? "bg-blue-100" : "bg-green-100"
            }`}
          >
            <MdSystemUpdate
              size={20}
              className={
                updateAvailable ? "text-blue-600" : "text-green-600"
              }
            />
          </div>
          <p className="text-xs text-gray-500">Version</p>
          <p
            className={`text-sm font-semibold ${
              updateAvailable ? "text-blue-600" : "text-green-600"
            }`}
          >
            {updateAvailable ? "Update Ready" : "Up to Date"}
          </p>
        </div>
      </div>

      {/* Notification Settings */}
      <div className="mb-6">
        <h3 className="text-md font-semibold text-gray-700 mb-3">
          Notifications
        </h3>
        <NotificationSettings />
      </div>

      {/* Install App */}
      {!isInstalled && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <MdGetApp size={28} className="text-blue-600" />
              <div>
                <p className="font-semibold text-gray-800">
                  Install SarifiMesh
                </p>
                <p className="text-sm text-gray-500">
                  Install as an app for the best experience
                </p>
              </div>
            </div>
            {isInstallable ? (
              <button
                onClick={installPWA}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-700"
              >
                Install Now
              </button>
            ) : (
              <span className="text-xs text-gray-400 italic">
                Use browser menu to install
              </span>
            )}
          </div>
        </div>
      )}

      {/* Update Available */}
      {updateAvailable && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-5 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-semibold text-gray-800">Update Available</p>
              <p className="text-sm text-gray-500">
                A new version of SarifiMesh is ready
              </p>
            </div>
            <button
              onClick={applyUpdate}
              className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-green-700"
            >
              Update Now
            </button>
          </div>
        </div>
      )}

      {/* PWA Features */}
      <div>
        <h3 className="text-md font-semibold text-gray-700 mb-3">
          PWA Features
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {features.map((feature) => (
            <div
              key={feature.title}
              className="bg-white rounded-xl shadow p-4 flex items-start gap-3"
            >
              <div className="bg-blue-50 p-2 rounded-lg">{feature.icon}</div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-800">
                  {feature.title}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">{feature.desc}</p>
              </div>
              <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">
                Active
              </span>
            </div>
          ))}
        </div>
      </div>
    </Layout>
  );
};

export default PWASettings;